import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { flattenErrors } from '@feathr/hooks';

import { validateAdTagCreative } from './CreativeModal.utils';

/** Parameters for the useCreativeModal hook. */
export interface IUseCreativeModalParams {
  /** The creative being edited. */
  creative?: DisplayCreative;
  /** Callback when the modal should close. */
  onClose: () => void;
  /** Optional callback when saving. */
  onSave?: () => void | Promise<void>;
  /** Fields to skip validation for. */
  skipValidationFields?: string[];
}

/** Return type for the useCreativeModal hook. */
export interface IUseCreativeModalReturn {
  /** Handler to cancel and close (discards dirty changes). */
  handleCancel: () => void;
  /** Handler to save and close. */
  handleSave: () => Promise<void>;
  /** Validation errors for the creative. */
  validationErrors: string[];
}

/**
 * Hook that manages CreativeModal state and handlers.
 *
 * Encapsulates:
 * - Cancel handler (discards dirty changes)
 * - Save handler
 * - Validation error computation
 *
 * @param params - Hook parameters
 * @returns State and handlers for the modal
 *
 * @example
 * const { handleCancel, handleSave, validationErrors } = useCreativeModal({
 *   creative,
 *   onClose,
 *   onSave,
 * });
 */
export function useCreativeModal({
  creative,
  onClose,
  onSave,
  skipValidationFields = [],
}: IUseCreativeModalParams): IUseCreativeModalReturn {
  const { t } = useTranslation();

  const handleCancel = useCallback((): void => {
    creative?.discardDirty();
    onClose();
  }, [creative, onClose]);

  const handleSave = useCallback(async (): Promise<void> => {
    await onSave?.();
    await creative?.patchDirty();
    onClose();
  }, [creative, onClose, onSave]);

  // Compute validation errors
  const allErrors = creative ? creative.validate([], false, 'grouped').errors : {};
  const filteredErrors = Object.fromEntries(
    Object.entries(allErrors).filter(([field]) => !skipValidationFields.includes(field)),
  );
  const validationErrors = flattenErrors(filteredErrors);

  // Add ad tag specific validation
  if (creative) {
    validationErrors.push(...validateAdTagCreative(creative, t));
  }

  return {
    handleCancel,
    handleSave,
    validationErrors,
  };
}
